import { useCallback } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { useRouter } from '@polyai/common/hooks/useRouter';
import { MAIN_BRANCH_ID } from '@polyai/stream/constants';

import {
  type UseAgentSetupReturn,
  useAgentSetup,
} from 'hooks/agentWizard/useAgentSetup';
import useProjectRoute from 'hooks/useProjectRoute';
import { generateNextPath } from 'lib/routeHelper';
import AGENT_WIZARD_PATHS from 'screens/AgentWizard/paths';

interface UseAgentSetupFlowReturn extends UseAgentSetupReturn {
  navigateToVoice: () => void;
  navigateToScenarios: () => Promise<void>;
}

export const useAgentSetupFlow = (): UseAgentSetupFlowReturn => {
  const { pushUrl } = useRouter();
  const { accountId, projectId } = useProjectRoute();
  const queryClient = useQueryClient();

  const baseSetup = useAgentSetup(projectId);

  const navigateToVoice = useCallback(() => {
    pushUrl(
      generateNextPath(AGENT_WIZARD_PATHS.VOICE, { accountId, projectId }),
    );
  }, [accountId, projectId, pushUrl]);

  const navigateToScenarios = useCallback(async () => {
    // Invalidate projection cache to ensure fresh data
    await queryClient.invalidateQueries({
      queryKey: [accountId, projectId, MAIN_BRANCH_ID, 'projection'],
    });

    // Navigate to Scenarios screen
    pushUrl(
      generateNextPath(AGENT_WIZARD_PATHS.SCENARIOS, { accountId, projectId }),
    );
  }, [accountId, projectId, pushUrl, queryClient]);

  return {
    ...baseSetup,
    navigateToVoice,
    navigateToScenarios,
  };
};
